import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { RR_LOGO_SRC } from '../assets/rr-logo.js';
import { useEscapeToClose } from '../hooks/useEscapeToClose.js';
import { RrThemeToggle } from './RrThemeToggle.js';
const NAV_BG = 'var(--rr-bg-nav)';
const NAV_FG = 'var(--rr-fg-on-nav)';
const ACCENT_PRIMARY = 'var(--rr-accent-primary)';
const ACCENT_LINK = 'var(--rr-accent-link)';
function defaultRenderLink(item, _isActive, children) {
    return (_jsx("a", { href: item.to, style: { color: 'inherit', textDecoration: 'none', display: 'block', width: '100%' }, children: children }));
}
function UserChip({ initials, role, themeToggleSlot, onSignOut, position, }) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    // Overnight D7 — Escape closes the menu (theme switch + Sign out) and puts
    // focus back on the button that opened it, so a keyboard user is not left
    // stranded. Click-outside below still closes it too.
    const closeAndRefocus = useCallback(() => {
        setOpen(false);
        triggerRef.current?.focus();
    }, []);
    useEscapeToClose(open, closeAndRefocus);
    useEffect(() => {
        if (!open)
            return;
        function handler(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);
    // Render placeholder when no user info — keeps the layout slot but
    // doesn't open an empty dropdown.
    if (!initials)
        return _jsx("div", { style: { width: '40px', height: '40px' }, "aria-hidden": true });
    return (_jsxs("div", { ref: wrapperRef, style: { position: 'relative' }, children: [_jsxs("button", { ref: triggerRef, type: "button", "aria-haspopup": "menu", "aria-expanded": open, onClick: () => setOpen(o => !o), style: {
                    fontFamily: 'var(--rr-font-mono)',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: NAV_FG,
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 'var(--rr-radius-md)',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--rr-space-2)',
                }, children: [_jsx("span", { "aria-hidden": true, style: { fontWeight: 600 }, children: initials }), role ? _jsx("span", { style: { opacity: 0.7 }, children: role }) : null] }), open ? (_jsxs("div", { role: "menu", style: {
                    position: 'absolute',
                    top: position === 'mobile' ? 'calc(100% + 8px)' : 'auto',
                    bottom: position === 'desktop' ? 'calc(100% + 8px)' : 'auto',
                    right: 0,
                    minWidth: '220px',
                    backgroundColor: 'var(--rr-bg-surface-raised)',
                    color: 'var(--rr-fg-primary)',
                    border: '1px solid var(--rr-border-default)',
                    borderRadius: 'var(--rr-radius-lg)',
                    padding: 'var(--rr-space-3)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--rr-space-2)',
                    zIndex: 100,
                }, children: [themeToggleSlot !== null ? (_jsxs("div", { children: [_jsx("div", { style: {
                                    fontFamily: 'var(--rr-font-mono)',
                                    fontSize: '10px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    color: 'var(--rr-fg-secondary)',
                                    marginBottom: 'var(--rr-space-1)',
                                }, children: "Theme" }), themeToggleSlot ?? _jsx(RrThemeToggle, { orientation: "vertical" })] })) : null, onSignOut ? (_jsx("button", { type: "button", role: "menuitem", onClick: () => {
                            setOpen(false);
                            onSignOut();
                        }, style: {
                            fontFamily: 'var(--rr-font-body)',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: 'var(--rr-fg-primary)',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderTop: themeToggleSlot !== null ? '1px solid var(--rr-border-default)' : 'none',
                            paddingTop: themeToggleSlot !== null ? 'var(--rr-space-2)' : 0,
                            textAlign: 'left',
                            cursor: 'pointer',
                            padding: 'var(--rr-space-2)',
                        }, children: "Sign out" })) : null] })) : null] }));
}
function Logo() {
    // T4_6-PIPE-LOGO-ASSET: real Right Role mark (RR_LOGO_SRC, SVG data URI).
    // Replaces the early-Phase-4.5 placeholder (orange box + "R" letter). Size
    // preserved at 28×28px per the prior placeholder; the SVG carries its own
    // orange background so border-radius rounds the branded square.
    return (_jsx("img", { src: RR_LOGO_SRC, alt: "", "aria-hidden": true, width: 28, height: 28, style: {
            width: '28px',
            height: '28px',
            borderRadius: 'var(--rr-radius-sm)',
            display: 'block',
            flexShrink: 0,
        } }));
}
/**
 * The top-left logo wrapped as a "home" link. Routes to `homeTo` (default
 * `/`) via the consumer's `renderLink` so it is client-side in router apps
 * and a plain `<a>` elsewhere. Carries a visually-hidden accessible name
 * since the logo image itself is decorative (aria-hidden).
 */
function HomeLogo({ homeTo, renderLink, }) {
    return (_jsx("span", { style: { display: 'inline-flex', alignItems: 'center', flexShrink: 0 }, children: renderLink({ id: '__home__', label: 'Home', to: homeTo }, false, _jsxs("span", { "aria-label": "Right Role \u2014 home", style: { display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }, children: [_jsx(Logo, {}), _jsx("span", { style: {
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0 0 0 0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                    }, children: "Home" })] })) }));
}
/** F-32: placeholder bars in the shape of the menu, shown until roles load. */
const SKELETON_WIDTHS = ['62%', '48%', '55%', '40%', '58%', '45%'];
function NavSkeleton({ orientation }) {
    return (_jsx("div", { role: "status", "aria-busy": "true", "aria-label": "Loading menu", "data-rr-nav-skeleton": true, style: {
            display: 'flex',
            flexDirection: 'column',
            gap: orientation === 'desktop' ? 'var(--rr-space-3)' : 'var(--rr-space-2)',
            padding: '0 var(--rr-space-4)',
        }, children: SKELETON_WIDTHS.map((width, i) => (_jsx("div", { "aria-hidden": true, "data-rr-nav-skeleton-bar": true, style: {
                width,
                height: '12px',
                margin: 'var(--rr-space-2) 0',
                borderRadius: 'var(--rr-radius-sm)',
                backgroundColor: 'rgba(255, 255, 255, 0.14)',
            } }, i))) }));
}
function NavItems({ sections, activeItemId, renderLink, orientation, loading = false, }) {
    if (loading)
        return _jsx(NavSkeleton, { orientation: orientation });
    return (_jsx("nav", { "aria-label": "Primary", style: {
            display: 'flex',
            flexDirection: 'column',
            gap: orientation === 'desktop' ? 'var(--rr-space-4)' : 'var(--rr-space-2)',
        }, children: sections.map((section, sectionIdx) => (_jsxs("div", { children: [section.title && orientation === 'desktop' ? (_jsx("div", { style: {
                        fontFamily: 'var(--rr-font-mono)',
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.14em',
                        // Lifted 0.5 → 0.62 so section labels (PIPELINE / FINANCE / …)
                        // are legible rather than ghosted on the dark sidebar.
                        color: 'rgba(255, 255, 255, 0.62)',
                        padding: '0 var(--rr-space-4) var(--rr-space-1)',
                    }, children: section.title })) : null, section.items.map(item => {
                    const isActive = item.id === activeItemId;
                    return (_jsx("div", { "data-rr-nav-item": true, "data-active": isActive ? 'true' : undefined, style: {
                            position: 'relative',
                            fontFamily: 'var(--rr-font-body)',
                            fontSize: '13px',
                            // Resting contrast lifted (0.75 → 0.82) so the menu reads
                            // less murky on the dark sidebar; hover/active handled in the
                            // <style> block below (inline styles can't express :hover).
                            color: isActive ? '#FFFFFF' : 'rgba(255, 255, 255, 0.82)',
                            backgroundColor: isActive ? 'rgba(255, 255, 255, 0.10)' : 'transparent',
                            borderLeft: isActive ? `3px solid ${ACCENT_LINK}` : '3px solid transparent',
                            padding: 'var(--rr-space-2) var(--rr-space-4)',
                            borderRadius: 'var(--rr-radius-sm)',
                            transition: 'background-color 120ms ease, color 120ms ease',
                        }, children: renderLink(item, isActive, _jsxs("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 'var(--rr-space-2)' }, children: [item.icon, item.label] })) }, item.id));
                })] }, section.title ?? `unnamed-${sectionIdx}`))) }));
}
export function RrNav({ productName, sections, loading = false, activeItemId, userInitials, userRole, onSignOut, themeToggleSlot, renderLink = defaultRenderLink, homeTo = '/', children, }) {
    // Mobile drawer open state (only relevant <768px).
    const [drawerOpen, setDrawerOpen] = useState(false);
    return (_jsxs("div", { "data-rr-nav": true, style: {
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
        }, children: [_jsxs("header", { "data-rr-nav-mobile": true, role: "banner", style: {
                    height: '56px',
                    backgroundColor: NAV_BG,
                    color: NAV_FG,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 var(--rr-space-4)',
                    gap: 'var(--rr-space-3)',
                }, children: [_jsx("button", { type: "button", "aria-label": drawerOpen ? 'Close menu' : 'Open menu', "aria-expanded": drawerOpen, onClick: () => setDrawerOpen(o => !o), style: {
                            background: 'transparent',
                            border: 'none',
                            color: NAV_FG,
                            cursor: 'pointer',
                            fontSize: '24px',
                            lineHeight: 1,
                            padding: 'var(--rr-space-1)',
                        }, children: "\u2630" }), _jsx(HomeLogo, { homeTo: homeTo, renderLink: renderLink }), _jsx("h1", { "data-rr-nav-wordmark": true, style: {
                            fontFamily: 'var(--rr-font-display)',
                            fontSize: '18px',
                            letterSpacing: '0.04em',
                            // T10A-014 + header request: keep the wordmark on a single line
                            // (lineHeight:1 + nowrap) so it never wraps/clips inside the 56px
                            // bar. Rather than ellipsis-truncating to a partial word when the
                            // bar is tight, the wordmark is HIDDEN below 480px (see the @media
                            // block) so on narrow phones only the logo shows — never "RIGHT R…".
                            // It reappears in full once there's room (≥480px) and on desktop.
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            margin: 0,
                            color: NAV_FG,
                            textTransform: 'uppercase',
                        }, children: renderLink({ id: '__home_wordmark_mobile__', label: 'Home', to: homeTo }, false, _jsx("span", { style: { color: NAV_FG, textDecoration: 'none', cursor: 'pointer' }, children: productName })) }), _jsx("div", { "aria-hidden": true, style: { flex: 1, minWidth: 'var(--rr-space-2)' } }), _jsx(UserChip, { initials: userInitials, role: userRole, themeToggleSlot: themeToggleSlot, onSignOut: onSignOut, position: "mobile" })] }), _jsx("div", { "aria-hidden": true, "data-rr-nav-brand-strip": true, style: {
                    height: '4px',
                    backgroundColor: ACCENT_PRIMARY,
                } }), drawerOpen ? (_jsxs(_Fragment, { children: [_jsx("div", { "data-rr-nav-drawer-backdrop": true, "aria-hidden": true, onClick: () => setDrawerOpen(false), style: {
                            position: 'fixed',
                            top: '60px',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            zIndex: 50,
                        } }), _jsx("div", { "data-rr-nav-drawer": true, role: "dialog", "aria-label": "Navigation menu", onClick: () => setDrawerOpen(false), style: {
                            backgroundColor: NAV_BG,
                            color: NAV_FG,
                            padding: 'var(--rr-space-4)',
                            position: 'relative',
                            zIndex: 60,
                        }, children: _jsx(NavItems, { sections: sections, activeItemId: activeItemId, renderLink: renderLink, orientation: "mobile", loading: loading }) })] })) : null, _jsxs("div", { "data-rr-nav-shell": true, style: {
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                }, children: [_jsxs("aside", { "data-rr-nav-rail": true, "aria-label": "Primary navigation", style: {
                            display: 'none',
                            width: '240px',
                            flexShrink: 0,
                            backgroundColor: NAV_BG,
                            color: NAV_FG,
                            // `display` is set by the @media block (none below 768px, flex
                            // from 768px); the column layout applies once it is shown.
                            flexDirection: 'column',
                            // E2E-R2 #2 — pin the rail (and the profile chip at its bottom) to
                            // the viewport. A sticky, exactly-100vh column means the middle
                            // nav list scrolls internally (it already has overflowY:auto) while
                            // the user chip stays anchored at the bottom — no page-scroll to
                            // reach theme/sign-out.
                            height: '100vh',
                            position: 'sticky',
                            top: 0,
                            alignSelf: 'flex-start',
                        }, children: [_jsxs("div", { style: {
                                    padding: 'var(--rr-space-5) var(--rr-space-4) var(--rr-space-4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--rr-space-2)',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                }, children: [_jsx(HomeLogo, { homeTo: homeTo, renderLink: renderLink }), _jsx("h1", { style: {
                                            fontFamily: 'var(--rr-font-display)',
                                            fontSize: '18px',
                                            letterSpacing: '0.04em',
                                            margin: 0,
                                            color: NAV_FG,
                                            textTransform: 'uppercase',
                                        }, children: renderLink({ id: '__home_wordmark__', label: 'Home', to: homeTo }, false, _jsx("span", { style: { color: NAV_FG, textDecoration: 'none', cursor: 'pointer' }, children: productName })) })] }), _jsx("div", { style: { flex: 1, overflowY: 'auto', padding: 'var(--rr-space-4) 0' }, children: _jsx(NavItems, { sections: sections, activeItemId: activeItemId, renderLink: renderLink, orientation: "desktop", loading: loading }) }), _jsx("div", { style: {
                                    padding: 'var(--rr-space-4)',
                                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                                }, children: _jsx(UserChip, { initials: userInitials, role: userRole, themeToggleSlot: themeToggleSlot, onSignOut: onSignOut, position: "desktop" }) })] }), _jsx("main", { style: { flex: 1, minWidth: 0, overflowY: 'auto', backgroundColor: 'var(--rr-bg-page)' }, children: children })] }), _jsx("style", { children: `
        /* Nav-item hover feedback (dark-mode walk polish 2026-06-08). Inline
           styles can't express :hover, so the resting/active colors live on the
           element and the hover lift lives here. A non-active item gains a
           subtle white wash + full-white label on hover; active items already
           render at full white so the rule is harmless on them. Gives the menu
           the "this is clickable" feedback it previously lacked. */
        [data-rr-nav-item]:hover {
          background-color: rgba(255, 255, 255, 0.08) !important;
          color: #FFFFFF !important;
        }
        [data-rr-nav-item][data-active='true']:hover {
          background-color: rgba(255, 255, 255, 0.14) !important;
        }
        /* Header request: on narrow phones the wordmark can't fully fit, so
           hide it (logo-only) rather than clip a partial word. It reappears
           in full once there's room (≥480px) and on the desktop rail. */
        @media (max-width: 479.98px) {
          [data-rr-nav-wordmark] { display: none !important; }
        }
        @media (min-width: 768px) {
          [data-rr-nav-mobile] { display: none !important; }
          [data-rr-nav-brand-strip] { display: none !important; }
          [data-rr-nav-drawer] { display: none !important; }
          [data-rr-nav-drawer-backdrop] { display: none !important; }
          [data-rr-nav-rail] { display: flex !important; }
        }
      ` })] }));
}
//# sourceMappingURL=RrNav.js.map